/**
 * Requisitos de contraseña, en un solo sitio.
 *
 * Esta regla vivía duplicada en cinco pantallas, y las copias habían divergido
 * entre sí y del servidor. Dos consecuencias reales:
 *
 *  - Todas comprobaban el símbolo con `/[@$!%*?&]/`, una lista de siete
 *    caracteres. Una contraseña con `#`, `-` o `.` no marcaba el requisito,
 *    aunque el servidor (ya corregido) la acepte.
 *  - `register/page.tsx` no comprobaba la minúscula, requisito que el servidor
 *    sí exige. La interfaz mostraba sus cuatro requisitos en verde y el alta
 *    fallaba igual, sin nada en pantalla que explicara por qué.
 *
 * El contrato de abajo replica el del backend en
 * `therapyrecap-backend/routes/auth.routes.js#isStrongPassword`:
 *
 *     /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/
 *
 * La duplicación entre repositorios es inevitable —son dos despliegues— pero
 * que dentro de este haya una sola copia sí está en nuestra mano.
 */

export interface RequisitoDeContrasena {
    readonly id: string;
    /** Texto que ve el usuario en la lista de requisitos. */
    readonly etiqueta: string;
    readonly cumple: (contrasena: string) => boolean;
}

export const REQUISITOS_DE_CONTRASENA: readonly RequisitoDeContrasena[] = [
    {
        id: 'longitud',
        etiqueta: 'Al menos 8 caracteres',
        cumple: (contrasena) => contrasena.length >= 8,
    },
    {
        id: 'mayuscula',
        etiqueta: '1 Mayúscula',
        cumple: (contrasena) => /[A-Z]/.test(contrasena),
    },
    {
        id: 'minuscula',
        etiqueta: '1 Minúscula',
        cumple: (contrasena) => /[a-z]/.test(contrasena),
    },
    {
        id: 'numero',
        etiqueta: '1 Número',
        cumple: (contrasena) => /\d/.test(contrasena),
    },
    {
        // Por exclusión y no por lista blanca: cualquier cosa que no sea letra
        // ni dígito cuenta. Enumerar los símbolos aceptados es justo lo que
        // hacía rechazar contraseñas mejores que las que pasaban.
        id: 'simbolo',
        etiqueta: '1 Símbolo',
        cumple: (contrasena) => /[^A-Za-z\d]/.test(contrasena),
    },
];

export type EstadoDeRequisitos = Record<string, boolean>;

/**
 * Evalúa cada requisito por separado, para pintar la lista de la interfaz.
 */
export function evaluarRequisitos(contrasena: string): EstadoDeRequisitos {
    return Object.fromEntries(
        REQUISITOS_DE_CONTRASENA.map((requisito) => [requisito.id, requisito.cumple(contrasena)])
    );
}

/**
 * Responde si la contraseña sería aceptada por el servidor.
 */
export function esContrasenaFuerte(contrasena: string): boolean {
    if (typeof contrasena !== 'string') {
        return false;
    }

    return REQUISITOS_DE_CONTRASENA.every((requisito) => requisito.cumple(contrasena));
}
